import { createPortal } from 'react-dom';
import type { CSSProperties, ReactNode } from 'react';

export type DragPreviewLayerProps = {
  visible: boolean;
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  usePortal?: boolean;
  portalTarget?: HTMLElement | null;
};

/**
 * Floating drag preview layer that follows pointer/touch coordinates.
 */
export function DragPreviewLayer({
  visible,
  x,
  y,
  offsetX,
  offsetY,
  children,
  className = '',
  style,
  usePortal = true,
  portalTarget,
}: DragPreviewLayerProps): JSX.Element | null {
  if (!visible) return null;

  const targetElement =
    portalTarget || (typeof document !== 'undefined' ? document.body : null);

  const layer = (
    <div
      aria-hidden
      data-dnd-drag-preview="true"
      className={`pointer-events-none fixed left-0 top-0 z-[10001] ${className}`}
      style={{
        transform: `translate3d(${x - offsetX}px, ${y - offsetY}px, 0)`,
        ...style,
      }}
    >
      {children}
    </div>
  );

  if (!usePortal || !targetElement) {
    return layer;
  }

  return createPortal(layer, targetElement);
}
